import { typeboxResolver } from '@hookform/resolvers/typebox';
import { Static, Type } from '@sinclair/typebox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-use';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FormField, FormItem, Form, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { showErrorToast, toast } from '@/components/ui/use-toast';
import { projectHooks } from '@/hooks/project-hooks';
import { HttpError } from '@/lib/api';
import { projectApi } from '@/lib/project-api';

const FormSchema = Type.Object({
  name: Type.String({
    errorMessage: t('Please the name of your new project'),
    required: true,
  }),
});

type FormSchema = Static<typeof FormSchema>;

export function CreateProjectDialog() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { setCurrentProject } = projectHooks.useCurrentProject();

  const { mutate, isPending } = useMutation<void, HttpError, FormSchema>({
    mutationFn: async (data: FormSchema) => {
      const newProject = await projectApi.createUserProject({
        displayName: data.name,
      });
      setCurrentProject(queryClient, newProject, location.pathname);
    },
    onSuccess: () => {
      setIsOpen(false);
      toast({
        variant: 'success',
        title: t('Project created successfully'),
      });
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });

  const form = useForm<FormSchema>({
    resolver: typeboxResolver(FormSchema),
    defaultValues: {
      name: '',
    },
  });

  const onSubmit = (data: FormSchema) => {
    mutate(data);
  };

  const serverError = form.formState?.errors?.root?.serverError;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center justify-center gap-2 ml-3"
        >
          <Plus className="size-4" />
          {t('Create Project')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('Create New Project')}</DialogTitle>
          <DialogDescription>
            {t(
              'Enter the name of the project you want to create. You can invite users to the project after creating it.'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <Label htmlFor="name">{t('Name')}</Label>
                  <Input {...field} type="text" placeholder={t('My project')} />
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError && <FormMessage>{serverError.message}</FormMessage>}
            <DialogFooter>
              <Button type="submit" loading={isPending}>
                {t('Create Project')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
